import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'app-employee-status-card-view',
  templateUrl: './employee-status-card.component.html',
  styleUrls: ['./employee-status-card.component.scss'],
})
export class EmployeeStatusCardComponent {
  @Input() person: any;
  @Input() index: number = 0;

  @Output() statusClicked = new EventEmitter<{
    employeeId: string;
    status: 'pending' | 'inProgress' | 'completed';
  }>();

  protected colors = {
    active: '#28c76f',
    signedOut: '#a8d0ff',
    remote: '#4d2d6e',
    absent: '#e93015',
    present: '#e78127',
    idle: '#f0ad4e',
    break: '#FFA500',
  };

  private isAvailableProject(): boolean {
    const project = this.person?.projectName;
    return !project || project === 'Available';
  }

  private hasProject(): boolean {
    const project = this.person?.projectName;
    return !!project && project !== 'Available';
  }

  get displayName(): string {
    const empName = this.person?.employee;
    return Array.isArray(empName)
      ? empName.filter(Boolean).join(' ')
      : 'Unknown';
  }

  get statusText(): string {
    const { signIn, signOut, type } = this.person || {};
    if (this.hasProject()) return 'Working';
    if (signOut) return 'Signed Out';
    if (!signIn && this.isAvailableProject()) return 'Absent';
    if (signIn && this.isAvailableProject()) return 'Idle';
    return 'Idle';
  }

  get statusColor(): string {
    const { signIn, signOut, type } = this.person || {};
    if (this.hasProject()) return this.colors.active;
    if (signOut) return this.colors.signedOut;
    if (signIn && type?.trim() === 'Remote') return this.colors.remote;
    if (!signIn && this.isAvailableProject()) return this.colors.absent;
    if (signIn && this.isAvailableProject()) return this.colors.idle;
    return this.colors.idle;
  }

  get projectDisplay(): string {
    const name = this.person?.projectName?.trim();
    if (!name || name === 'Available' || name === '-') return '';
    return name;
  }

  get modeBanner(): { text: string; color: string } {
    const { signIn, signOut, type } = this.person || {};
    if (!signIn && !signOut) {
      return { text: 'Absent', color: this.colors.absent };
    }
    if (type?.trim() === 'Remote') {
      return { text: 'Remote', color: this.colors.remote };
    }
    if (type?.trim() === 'On Site') {
      return { text: 'On Site', color: this.colors.present };
    }
    return { text: 'On Site', color: this.colors.present };
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src =
      'assets/images/avatars/0.png';
  }

  get taskCounts() {
    return this.person?.taskCounts || {
      pendingAssigned: 0,
      inProgress: 0,
      completed: 0
    };
  }

  get totalTasks(): number {
    const { pendingAssigned, inProgress, completed } = this.taskCounts;
    return pendingAssigned + inProgress + completed;
  }

  get pendingPercent(): number {
    return this.totalTasks
      ? (this.taskCounts.pendingAssigned / this.totalTasks) * 100
      : 0;
  }

  get inProgressPercent(): number {
    return this.totalTasks
      ? (this.taskCounts.inProgress / this.totalTasks) * 100
      : 0;
  }

  get completedPercent(): number {
    return this.totalTasks
      ? (this.taskCounts.completed / this.totalTasks) * 100
      : 0;
  }

  onStatusClick(type: 'pending' | 'inProgress' | 'completed') {
    this.statusClicked.emit({
      employeeId: this.person.employeeId,
      status: type
    });
  }
}
